// src/lib/avoir-send-flow.js — L'ENVOI DE L'AVOIR au propriétaire.
//
// Chantier « L'avoir part chez le propriétaire » (décision Dimitri : « un
// avoir qu'il ne reçoit pas ne sert à rien — c'est la pièce qui lui explique
// pourquoi la première était fausse »). CHEMIN COMPTABLE (CGI 289).
//
// Même chemin qu'une facture — sendThenMarkDelivered (facture-send-flow.js) :
// l'envoi d'abord, la date de transmission SEULEMENT s'il a réussi. Rien n'est
// dupliqué ici, on injecte `send` / `markDelivered` exactement comme App.jsx le
// fait pour une facture. Ce qui change, c'est le CONTENU : une facture dit
// « voici ce que vous me devez » ; un avoir dit « la pièce N° X était fausse,
// voici son annulation ». Le corps du message NOMME donc la pièce annulée, son
// numéro et son montant, et dit ce qui la remplace — ou qu'une facture corrigée
// suivra.
//
// CE QUE CET ENVOI N'ÉCRIT JAMAIS : l'originale. Elle est `cancelled` (posé
// par la RPC emit_credit_note, seule transition que le trigger d'immuabilité
// admet), et TOUT ce que l'on veut savoir d'elle du point de vue du
// propriétaire se DÉRIVE de l'avoir : `related_invoice_id` dit laquelle,
// `delivered_at` de l'avoir dit quand l'explication lui est parvenue. Aucune
// colonne ne manque ; poser quoi que ce soit sur l'originale serait exactement
// ce que le trigger interdit — et `delivered_at` de l'originale porte la date
// de SA transmission, qui reste vraie (il l'a bien reçue). On ne la réécrit pas.
//
// Trois fonctions pures, calquées sur invoice-avoir-flow.js :
//   - describeAvoirEnvoi   : ce que l'écran dit AVANT le clic (quelle pièce
//                            part, à qui, et que l'originale reste chez lui).
//   - buildAvoirEmailParams: les variables du gabarit EmailJS — corps
//                            explicatif + document de l'avoir.
//   - sendAvoirCoherent    : orchestre le geste via sendThenMarkDelivered.

import { sendThenMarkDelivered } from "./facture-send-flow";
import { emailjsTemplateAvoir } from "./emailjs-loader";

const fmtEur = (n) =>
  `${Number(n || 0)
    .toFixed(2)
    .replace(".", ",")} €`;

const esc = (v) =>
  String(v ?? "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");

/**
 * Comparaison de deux noms de propriétaire, tolérante à ce qui ne veut rien
 * dire (casse, accents, espaces doubles) et à rien d'autre. Sert à détecter
 * qu'une FICHE a changé de mains entre l'émission de la pièce et son envoi.
 */
export function memeProprietaire(a, b) {
  const norm = (v) =>
    String(v ?? "")
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "")
      .replace(/\s+/g, " ")
      .trim()
      .toLowerCase();
  return norm(a) === norm(b);
}

/**
 * Ce que l'écran annonce avant le clic. Lecture pure, rien n'est écrit.
 *
 * @param {object} args
 * @param {object} args.avoir       - ligne Historique de l'AVOIR { num, kind,
 *   montant, mois, log, prop, invoiceId, emailEnvoye, originalNumber, relatedInvoiceId }
 * @param {object} [args.originale] - ligne Historique de la facture annulée
 *   (archive), si l'écran l'a ; sinon on se rabat sur avoir.originalNumber
 * @param {string} [args.emailDest] - destinataire résolu par l'appelant
 *   (meta.email_dest de l'avoir, sinon l'e-mail courant du propriétaire)
 * @param {object} [args.remplacante] - facture active qui remplace l'originale
 *   pour la même période, si elle existe { num, montant }
 * @returns {{ possible, raison, emailDest, dejaTransmis, originalNumber,
 *   montantOriginal, faits: string[] }}
 */
export function describeAvoirEnvoi({
  avoir,
  originale = null,
  emailDest = null,
  remplacante = null,
  propActuel = null,
  templateAvoir = emailjsTemplateAvoir(),
} = {}) {
  const a = avoir || {};
  const originalNumber =
    (originale && originale.num) || a.originalNumber || null;
  const montantOriginal =
    originale && Number.isFinite(Number(originale.montant))
      ? Number(originale.montant)
      : Math.abs(Number(a.montant) || 0);
  const base = {
    emailDest: emailDest || null,
    dejaTransmis: !!a.emailEnvoye,
    originalNumber,
    montantOriginal,
    faits: [],
  };
  if (a.kind !== "credit_note") {
    return {
      ...base,
      possible: false,
      raison:
        "Seul un avoir part par ce chemin — une facture s'envoie depuis l'onglet Facturation.",
    };
  }
  if (!a.invoiceId || !a.num) {
    return {
      ...base,
      possible: false,
      raison: "Avoir sans ligne en base ou sans numéro : rien ne peut partir.",
    };
  }
  if (!originalNumber) {
    return {
      ...base,
      possible: false,
      raison:
        "Cet avoir ne nomme pas la pièce qu'il annule : on n'envoie pas une annulation muette.",
    };
  }
  if (!emailDest) {
    return {
      ...base,
      possible: false,
      raison:
        "Aucune adresse e-mail pour ce propriétaire (ajoute-la dans Params) : rien ne part, rien n'est marqué.",
    };
  }
  // LA FICHE A-T-ELLE CHANGÉ DE MAINS ? (revue adverse M3)
  // `avoir.prop` vient de `meta.prop_nom`, FIGÉ au moment de l'émission ;
  // `propActuel` est le propriétaire que l'écran résout AUJOURD'HUI dans cfg,
  // et c'est de LUI que vient l'adresse (aucune des factures en base ne porte
  // `meta.email_dest` : le repli cfg est le chemin nominal, pas l'exception).
  // Si les deux diffèrent, l'adresse appartient au NOUVEAU propriétaire et la
  // pièce au précédent : envoyer, c'est divulguer les comptes d'un tiers.
  // On refuse, on le dit, et on laisse l'envoi manuel — qui, lui, sait à qui.
  if (propActuel && a.prop && !memeProprietaire(a.prop, propActuel)) {
    return {
      ...base,
      possible: false,
      raison: `Cet avoir a été émis pour ${a.prop}, mais la fiche appartient aujourd'hui à ${propActuel} : la fiche a changé de propriétaire depuis l'émission — envoi manuel. Rien ne part (l'adresse connue est celle du nouveau propriétaire, la pièce concerne l'ancien).`,
    };
  }
  // Le gabarit DÉDIÉ à l'avoir (décision Dimitri 2026-09-16). Pas de repli sur
  // celui des factures : un propriétaire qui reçoit un mail dont l'objet dit
  // « Facture N° AV-… » lit l'inverse de ce que la pièce raconte. Mieux vaut
  // ne rien envoyer et le dire.
  if (!templateAvoir) {
    return {
      ...base,
      possible: false,
      raison:
        "Gabarit d'avoir non configuré (REACT_APP_EMAILJS_TEMPLATE_AVOIR) : rien ne part. On n'envoie pas un avoir sous l'objet d'une facture — le propriétaire lirait « Facture N° AV-… », c'est faux.",
    };
  }
  const faits = [
    `Ce qui part : l'avoir N° ${a.num} (${fmtEur(a.montant)}), qui annule la facture N° ${originalNumber} de ${fmtEur(montantOriginal)} — ${a.mois || "?"} — ${a.log || a.prop || "?"}.`,
    `À qui : ${emailDest}. Le message nomme la pièce annulée, son numéro et son montant${remplacante && remplacante.num ? `, et la facture N° ${remplacante.num} qui la remplace` : ", et précise qu'une facture corrigée suivra s'il reste un montant dû"}.`,
    `La facture N° ${originalNumber} RESTE chez le propriétaire : on n'envoie pas un effacement, on envoie une explication. Elle n'est ni modifiée ni rappelée — l'avoir en est la contrepartie.`,
    a.emailEnvoye
      ? `Cet avoir a déjà été transmis : ce renvoi le fait repartir, la date de première transmission est conservée (aucune écriture).`
      : `Après l'envoi — et seulement s'il réussit — l'avoir reçoit sa date de transmission (comme une facture). Un envoi qui échoue n'écrit rien.`,
  ];
  return { ...base, possible: true, raison: null, faits, templateAvoir };
}

/**
 * Le corps explicatif du message : ce qui distingue l'envoi d'un avoir de
 * l'envoi d'une facture. Il NOMME la pièce annulée, son numéro, son montant,
 * et dit ce qui la remplace — ou qu'une facture corrigée suivra.
 *
 * Retourne un bloc HTML autonome, placé AU-DESSUS du document de l'avoir dans
 * la variable `facture_html` du gabarit EmailJS (le gabarit hébergé est le
 * même que celui d'une facture : ses variables sont figées côté EmailJS,
 * l'explication voyage donc dans le corps).
 */
export function genAvoirEmailIntroHTML({
  avoir,
  originalNumber,
  montantOriginal,
  remplacante = null,
}) {
  const a = avoir || {};
  const remplacement =
    remplacante && remplacante.num
      ? `Elle est remplacée par la facture <strong>N° ${esc(remplacante.num)}</strong>${Number.isFinite(Number(remplacante.montant)) ? ` d'un montant de ${esc(fmtEur(remplacante.montant))}` : ""}, qui vous est transmise séparément.`
      : `Aucune facture corrigée n'accompagne cet avoir pour l'instant : si un montant reste dû pour cette période, une facture corrigée vous parviendra séparément, sous un nouveau numéro.`;
  return `<div style="font-family:Raleway,Arial,sans-serif;font-size:13px;line-height:1.6;color:#333;max-width:700px;margin:0 auto 16px;padding:14px 18px;background:#fff8e6;border:1px solid #e9c46a;border-radius:10px">
  <p style="margin:0 0 8px"><strong style="color:#215387">Avoir N° ${esc(a.num)}</strong> — annulation de la facture <strong>N° ${esc(originalNumber)}</strong></p>
  <p style="margin:0 0 8px">Bonjour${a.prop ? ` ${esc(a.prop)}` : ""},</p>
  <p style="margin:0 0 8px">La facture <strong>N° ${esc(originalNumber)}</strong> (${esc(a.mois || "")}${a.log ? ` — ${esc(a.log)}` : ""}), d'un montant de <strong>${esc(fmtEur(montantOriginal))}</strong>, comportait une erreur. Le présent avoir <strong>N° ${esc(a.num)}</strong>, d'un montant de <strong>${esc(fmtEur(a.montant))}</strong>, l'annule intégralement.</p>
  <p style="margin:0 0 8px">${remplacement}</p>
  <p style="margin:0">La facture N° ${esc(originalNumber)} que vous avez reçue reste valable comme pièce d'archive : elle n'est ni modifiée ni à détruire — conservez-la avec cet avoir, qui en est la contrepartie.</p>
</div>`;
}

/**
 * Les variables du gabarit EmailJS, dans le MÊME contrat que l'envoi d'une
 * facture (proprio / numero / logement / mois / email_destinataire /
 * facture_html) — le gabarit hébergé ne connaît que celles-là.
 *
 * @param {object} args
 * @param {object} args.avoir
 * @param {string} args.emailDest
 * @param {string} args.avoirHtml     - document de l'avoir, DÉJÀ gonflé (logo)
 * @param {object} [args.originale]
 * @param {object} [args.remplacante]
 */
export function buildAvoirEmailParams({
  avoir,
  emailDest,
  avoirHtml,
  originale = null,
  remplacante = null,
}) {
  const a = avoir || {};
  if (!a.num) throw new Error("buildAvoirEmailParams : avoir sans numéro");
  if (!emailDest)
    throw new Error("buildAvoirEmailParams : destinataire requis");
  const originalNumber = (originale && originale.num) || a.originalNumber;
  if (!originalNumber)
    throw new Error("buildAvoirEmailParams : pièce annulée inconnue");
  const montantOriginal =
    originale && Number.isFinite(Number(originale.montant))
      ? Number(originale.montant)
      : Math.abs(Number(a.montant) || 0);
  return {
    proprio: a.prop || "",
    numero: a.num,
    logement: a.log || "",
    mois: a.mois || "",
    email_destinataire: emailDest,
    facture_html:
      genAvoirEmailIntroHTML({
        avoir: a,
        originalNumber,
        montantOriginal,
        remplacante,
      }) + (avoirHtml || ""),
  };
}

/**
 * Envoie l'avoir puis pose sa date de transmission — via sendThenMarkDelivered,
 * le chemin d'une facture. Ordre : refus locaux → envoi → marquage → retour.
 *
 * Dépendances injectées (App.jsx fournit les implémentations réelles) :
 *   - send(params)        → Promise ; l'envoi réel (EmailJS). Reçoit les
 *                           variables bâties par buildAvoirEmailParams.
 *   - markDelivered(id)   → Promise<{ ok, message }> ; setInvoiceDelivered
 *                           sur l'AVOIR (jamais sur l'originale : ce flux ne
 *                           connaît même pas son id en écriture).
 *   - loadAvoirHtml(id)   → Promise<string> ; le document archivé de l'avoir,
 *                           gonflé (logo) par l'appelant.
 *   - onError(message) / onSuccess(result) / done() — done() toujours appelé.
 */
export async function sendAvoirCoherent(
  avoir,
  {
    emailDest,
    originale = null,
    remplacante = null,
    propActuel = null,
    send,
    markDelivered,
    loadAvoirHtml,
    onError,
    onSuccess,
    done,
  },
) {
  if (!avoir) {
    done();
    return;
  }
  const desc = describeAvoirEnvoi({
    avoir,
    originale,
    emailDest,
    remplacante,
    propActuel,
  });
  if (!desc.possible) {
    onError(desc.raison);
    done();
    return;
  }
  try {
    const avoirHtml = await loadAvoirHtml(avoir.invoiceId);
    if (!avoirHtml) {
      onError("Document de l'avoir introuvable en base : rien ne part.");
      done();
      return;
    }
    const params = buildAvoirEmailParams({
      avoir,
      emailDest,
      avoirHtml,
      originale,
      remplacante,
    });
    // Un avoir DÉJÀ transmis qui repart : la date de première transmission
    // fait foi, on ne la réécrit pas (0 écriture). Le chemin reste celui
    // d'une facture — sendThenMarkDelivered — avec un marqueur no-op.
    const transmission = await sendThenMarkDelivered({
      invoiceId: avoir.invoiceId,
      send: () => send(params, desc.templateAvoir),
      markDelivered: desc.dejaTransmis
        ? async () => ({ ok: true })
        : markDelivered,
    });
    onSuccess({
      ...transmission,
      resent: desc.dejaTransmis,
      emailDest,
      originalNumber: desc.originalNumber,
    });
    done();
  } catch (e) {
    console.error("[sendAvoirCoherent] envoi échoué (rien n’est marqué) :", e);
    onError(
      "L'avoir n'est PAS parti, et rien n'a été marqué : " +
        (e?.text || e?.message || JSON.stringify(e)),
    );
    done();
  }
}
